import React from 'react';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { useCurrentRefinements, useRefinementList } from '../../local-store';
import { useProductList } from '../../context';
import useRefinementHelpers from '../../hooks/useRefinementHelpers';
import { FacetProps } from './types';

/**
 * Collection-PLP facet: a row of pills with a leading "All", not the checkbox/accordion the category PLP
 * uses. Ported from the (since removed) client-side collection listing onto the SSR store.
 *
 * Labels are the last segment of a hierarchical value, so `Laundry Tubs > Tubs` shows as `Tubs`.
 */
const CollectionTermFacet: React.FC<FacetProps> = ({ attribute }) => {
  const { refine, items } = useRefinementList({ attribute, limit: 999, sortBy: ['name'] });
  const { resolveLabel } = useRefinementHelpers();
  const { removeAllRefinements } = useProductList();
  const { isTouchDevice } = useTouchDevice();
  const { items: currentRefinements } = useCurrentRefinements();

  const noRefinement = currentRefinements.length === 0;
  const pill = (active: boolean) =>
    `rounded-full border border-gray-700 px-16 py-10 text-12 transition 2xl:py-12 2xl:text-16 ${
      active ? 'bg-gray-700 text-white' : 'bg-transparent text-primary-black'
    } ${!isTouchDevice ? 'hover:!bg-gray-700 hover:text-white' : ''}`;

  return (
    <div>
      <div className="flex flex-row flex-wrap justify-start gap-12 md:justify-center lg:min-w-[20rem]">
        <div onClick={removeAllRefinements} className={`${pill(noRefinement)} hover:xl:cursor-pointer`}>
          All
        </div>
        {items.map((term) => (
          <div
            key={term.value}
            onClick={() => refine(term.value)}
            className="flex items-center justify-start hover:xl:cursor-pointer"
          >
            <div className={pill(term.isRefined)}>{resolveLabel(attribute, term.label)?.split('>')?.pop()}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CollectionTermFacet;
